import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useCallback, useContext, useEffect, useState } from "react";
import { BottomSheet } from "../../components/form/company-dropdown";
import {
  CognitoContext,
  UserProviderActions,
} from "../../contexts/cognito/cognito-provider";
import { mapGroupCompanyOptions } from "../../library/core/current-company";
import { PASSWORD_HINT, isValidPassword } from "../../library/core/utils";
import { CoreServiceSelector } from "../../services/core/service-selector";
import { TagEditor } from "../common/tag-editor";
import { CompanySelectUserForm } from "../form/company-dropdown";
import { ValidateFormView } from "../form/core";
import { FormRenderer } from "../form/form-render";
import { PrimaryButton } from "../form/primary-button";
import { SecondaryButton } from "../form/secondary-button";
import { ToggleView } from "../views/toggle-view";
import { CheckBox } from "./check-box";
import DeleteUserBottomSheetContent from "./delete-user-bottomsheet-content";

//https://github.com/react-tags/react-tags

const getFormView = ({ isNew }) => {
  const fields = [
    {
      name: "Email",
      label: "Email",
      type: "email",
      required: true,
      readOnly: !isNew,
      disabled: !isNew,
      isTopField: false,
    },
    {
      name: "FirstName",
      label: "First name",
      type: "text",
      required: true,
      isTopField: true,
    },
    {
      name: "LastName",
      label: "Last name",
      type: "text",
      required: true,
      isTopField: true,
    },
  ];
  if (isNew) {
    fields.push({
      name: "TempPassword",
      label: "Temporary Password",
      type: "text",
      required: true,
      isTopField: false,
      isPassword: true,
      validate: (value) => {
        if (!isValidPassword(value)) return PASSWORD_HINT;
        return null;
      },
    });
  }

  return {
    fields,
  };
};

const userRoles = [
  { name: "redeem", label: "Can redeem cards and certificates" },
  { name: "history", label: "Can view redemption history" },
  { name: "useradmin", label: "Can manage all users and their access" },
];

const PortalUserForm = ({
  isNew,
  data,
  onLoading,
  onSave,
  onEnableAccount,
  onDelete,
}) => {
  if (isNew) {
    data.UserRoles ||= "redeem,history";
  }
  const [isMyAccount, setIsMyAccount] = useState(false);
  const [model, setModel] = useState({});
  const [open, setOpen] = useState(false);
  const [dialogState, setDialogState] = useState({});
  const [companyOptions, setCompanyOptions] = useState({});
  const [modelErrors, setModelErrors] = useState({});
  const [refreshKey, setRefreshKey] = useState(1);
  const { state, dispatch } = useContext(CognitoContext);
  const formView = getFormView({ isNew });

  const getUserInfo = async (forceRefresh, refreshPage) => {
    if (!state.session) return;

    const userInfoService = CoreServiceSelector.GetUserInfoService(
      state.session
    );

    let userInfo = state.userInfo || userInfoService.getSavedUserInfo();

    if (forceRefresh || !userInfo) {
      userInfo = await userInfoService
        .getUserInfo(forceRefresh)
        .catch(console.error);

      dispatch({ type: UserProviderActions.setUserInfo, userInfo });
      if (!userInfo) return;
    }

    if (userInfo) {
      const gOptions = {
        options: mapGroupCompanyOptions(userInfo.GroupCompanies),
        defaultCompanyId: userInfo.CompanyId,
        readOnly:
          !userInfo.HasFullGroupAccess || userInfo.GroupCompanies.length < 2,
      };
      setCompanyOptions(gOptions);

      if (refreshPage) {
        setRefreshKey(1 + refreshKey);
      }
    }

    onLoading && onLoading(false);
  };

  const onSubmitHandler = (event) => {
    event.preventDefault();
    const errs = ValidateFormView({ fields: formView.fields, model });
    setModelErrors(errs);

    if (!errs || Object.keys(errs).length === 0) {
      onSave && onSave(model);
    }
  };

  const onFieldChangeHandler = (event) => {
    setModel({
      ...model,
      [event.target.name]: event.target.value,
    });
  };

  const onSetTargetCompany = (companyId) => {
    setModel({
      ...model,
      PartnerCompanyId: companyId,
    });
  };

  useEffect(() => {
    getUserInfo(false, true);
  }, [state]);

  useEffect(() => {
    if (isNew || !state.session || !state.userInfo || !model || !model.Email)
      return;

    setIsMyAccount(state.userInfo.Email === model.Email);
  }, [model, state]);

  useEffect(() => {
    setModel({
      ...data,
      PartnerCompanyId:
        data.PartnerCompanyId || companyOptions.defaultCompanyId,
    });
  }, [data, companyOptions]);

  const onUserRolesChanged = (value, oldValue) => {
    if (value === "") return;

    setModel({
      ...model,
      UserRoles: value,
    });
  };

  const onEnableAccountHandler = () => {
    onEnableAccount && onEnableAccount(model);
  };

  const onDismissDialog = () => {
    setDialogState({});
  };

  const onGeneratePasswordRequested = () => {
    const length = 10;
    const numbers = "0123456789";
    const symbols = "!@#$%^&*()_+-=[]{}|;:,.<>?";
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const allChars = numbers + symbols + uppercase + lowercase;

    function getRandom(str) {
      return str[Math.floor(Math.random() * str.length)];
    }

    let password = "";
    password += getRandom(numbers);
    password += getRandom(symbols);
    password += getRandom(uppercase);
    password += getRandom(lowercase);
    for (let i = password.length; i < length; i++) {
      password += getRandom(allChars);
    }
    password = password
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");

    setModel({
      ...model,
      TempPassword: password,
    });
  };

  const onConfirmDeleteUser = useCallback(() => {
    setOpen(false);
    onDismissDialog();
    onDelete && onDelete(model);
  }, [onDelete, model]);

  const onDeleteUserRequested = useCallback(() => {
    setOpen(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <form>
      <BottomSheet open={open} title=" " onClose={handleCancelDelete}>
        <DeleteUserBottomSheetContent
          model={model}
          onCancel={handleCancelDelete}
          onDelete={onConfirmDeleteUser}
        />
      </BottomSheet>
      {isMyAccount ? (
        <div className="alert alert-warning">
          <div>
            <h5>Caution</h5>
            <div>
              <span
                className="bi bi-exclamation-circle-fill"
                style={{ marginRight: "10px", fontSize: "18px" }}
              />
              You are editing your own account! Be careful when changing your
              access level.
            </div>
          </div>
        </div>
      ) : null}
      {/* <AccountStatus
        dataModel={model}
        label="Account Status"
        show={!isNew}
        onEnableAccount={onEnableAccountHandler}
      /> */}

      <FormRenderer
        formView={formView}
        isTopField={true}
        dataModel={model}
        errorsModel={modelErrors}
        onChange={onFieldChangeHandler}
      />
      <div>
        <label
          className="font-headings-primary text-[14px] mb-2"
          htmlFor="company-select-input"
        >
          Default venue
        </label>
        <CompanySelectUserForm
          label="Venue"
          title={"List venues"}
          companyIdFieldName="PartnerCompanyId"
          key={refreshKey}
          dataModel={model}
          showAvatar={false}
          customClassName={[
            "!h-auto !min-h-[50px] !px-5 mb-3",
            "!bg-[#0D0E1D] !rounded-2xl",
            "!ring-1 !ring-[#34353F] hover:!bg-white/5",
          ].join(" ")}
          sideIcon={
            <>
              <ExpandMoreIcon className="text-xl mr-2" />
            </>
          }
          {...companyOptions}
          onChange={onSetTargetCompany}
        />
      </div>

      <FormRenderer
        formView={formView}
        isTopField={false}
        dataModel={model}
        errorsModel={modelErrors}
        onChange={onFieldChangeHandler}
      />

      {isNew && (
        <SecondaryButton
          type="button"
          onClick={onGeneratePasswordRequested}
          className="mt-4 mb-4"
        >
          Suggest password
        </SecondaryButton>
      )}

      <TagEditor
        value={model.UserRoles}
        options={userRoles}
        onChange={onUserRolesChanged}
      />

      <CheckBox
        show={companyOptions.options && companyOptions.options.length > 1}
        dataModel={model}
        name="HasFullGroupAccess"
        label="Has access to all group venues"
        onChange={onFieldChangeHandler}
      />

      <PrimaryButton type="submit" onClick={onSubmitHandler}>
        {isNew ? "Create User" : "Save"}
      </PrimaryButton>
      <ToggleView
        show={!isNew && !isMyAccount && dialogState.action !== "delete-user"}
      >
        <SecondaryButton
          type="button"
          onClick={onDeleteUserRequested}
          className="mt-4"
        >
          Delete User
        </SecondaryButton>
      </ToggleView>
      <div className="h-[10vh]"></div>
    </form>
  );
};

export { PortalUserForm };

